// TelcoTrack — ROZVADEC weld-layout v2 contract (offers, cassettes, terminations, links).
// Cassettes occupy rack positions; terminations live in cassette slots. Direct welds
// are either incoming↔incoming pairs inside a KM4/KM6 tray (`cassetteId` + `peerIncoming`)
// or incoming→building (secondary `target`). Tray capacity is the aggregate of installed trays.
// New writes never carry unitLabel. OS is direct-only. v1 layouts were discarded.

import type { RozvadecMaterialUsageLike, RozvadecSplitterRatio } from './rozvadec-cabinet.constants';
import type { RozvadecCassetteTypeId } from './rozvadec-cassette.constants';
import type {
  OccupancyPatchConnection,
  OccupancySplitterPath,
  OccupancyWeldConnection,
} from './rozvadec-occupancy-connection.types';

export const ROZVADEC_WELD_LAYOUT_VERSION = 2 as const;
export type RozvadecWeldLayoutVersion = typeof ROZVADEC_WELD_LAYOUT_VERSION;

/** Rack position of a cassette is 0-based within its section (numbering order). */
export const ROZVADEC_WELD_CASSETTE_POSITION_BASE = 0;
/** Slot inside a cassette (adapter / preinstalled splitter) is 1-based. */
export const ROZVADEC_WELD_CASSETTE_SLOT_BASE = 1;
/** Splitter outputs are 1..N (ratio `1:N`). */
export const ROZVADEC_WELD_LAYOUT_SPLITTER_OUTPUT_BASE = 1;

export const ROZVADEC_WELD_TERMINATION_KINDS = ['connector', 'splitter'] as const;
export type RozvadecWeldTerminationKind = (typeof ROZVADEC_WELD_TERMINATION_KINDS)[number];

export const ROZVADEC_WELD_LINK_KINDS = ['direct', 'connector', 'splitter'] as const;
export type RozvadecWeldLinkKind = (typeof ROZVADEC_WELD_LINK_KINDS)[number];

export const ROZVADEC_WELD_LAYOUT_ISSUE_CODES = [
  'VERSION_UNSUPPORTED',
  'OFFERS_NONE_ENABLED',
  'OFFERS_OS_DIRECT_ONLY',
  'OFFERS_CONNECTORS_DISABLED',
  'OFFERS_DIRECT_DISABLED',
  'CASSETTE_TYPE_INVALID',
  'CASSETTE_POSITION_INVALID',
  'CASSETTE_POSITION_OVERLAP',
  'CASSETTE_SPAN_OUT_OF_SECTION',
  'CASSETTE_WELD_CAPACITY_EXCEEDED',
  'CASSETTE_ADAPTER_CAPACITY_EXCEEDED',
  'CASSETTE_SPLITTER_NOT_PREINSTALLED',
  'TERMINATION_KIND_INVALID',
  'TERMINATION_KIND_NOT_ALLOWED',
  'TERMINATION_CASSETTE_MISSING',
  'TERMINATION_CONTENT_NOT_ALLOWED',
  'TERMINATION_SLOT_OCCUPIED',
  'TERMINATION_INCOMING_INVALID',
  'TERMINATION_INCOMING_OCCUPIED',
  'TERMINATION_INCOMING_UNKNOWN',
  'TERMINATION_INCOMING_CAPACITY_UNKNOWN',
  'TERMINATION_INCOMING_OUT_OF_CAPACITY',
  'TERMINATION_RATIO_REQUIRED',
  'TERMINATION_RATIO_FORBIDDEN',
  'TERMINATION_RATIO_INVALID',
  'LINK_KIND_INVALID',
  'LINK_KIND_NOT_ALLOWED',
  'LINK_SOURCE_INVALID',
  'LINK_SOURCE_OCCUPIED',
  'LINK_TERMINATION_MISSING',
  'LINK_TERMINATION_KIND_MISMATCH',
  'LINK_SPLITTER_OUTPUT_INVALID',
  'LINK_SPLITTER_OUTPUT_OCCUPIED',
  'LINK_DIRECT_SOURCE_TERMINATED',
  'LINK_DIRECT_SHAPE_INVALID',
  'LINK_CASSETTE_MISSING',
  'LINK_CASSETTE_NOT_WELD_TRAY',
  'LINK_SEAT_INVALID',
  'LINK_SEAT_OCCUPIED',
  'LINK_PEER_INVALID',
  'LINK_PEER_SAME_AS_SOURCE',
  'LINK_TARGET_INVALID',
  'LINK_TARGET_OCCUPIED',
  'LINK_TARGET_BUILDING_UNKNOWN',
  'LINK_TARGET_NO_CABLE',
  'LINK_TARGET_CABLE_MISMATCH',
  'LINK_TARGET_CAPACITY_UNKNOWN',
  'LINK_TARGET_OUT_OF_CAPACITY',
  'LINK_UNIT_LABEL_FORBIDDEN',
  'CAPACITY_PRIMARY_EXCEEDED',
  'CAPACITY_SECONDARY_EXCEEDED',
  'CAPACITY_CONNECTOR_EXCEEDED',
  'CAPACITY_OS_UNKNOWN',
] as const;
export type RozvadecWeldLayoutIssueCode = (typeof ROZVADEC_WELD_LAYOUT_ISSUE_CODES)[number];

/** Completeness / “can confirm” only — persist and save/close must not fail on these. */
export const ROZVADEC_WELD_LAYOUT_COMPLETENESS_ISSUE_CODES = [
  'LINK_TARGET_INVALID',
  'LINK_TARGET_NO_CABLE',
  'LINK_TARGET_CAPACITY_UNKNOWN',
  'LINK_PEER_INVALID',
  'TERMINATION_INCOMING_INVALID',
  'TERMINATION_INCOMING_CAPACITY_UNKNOWN',
  'CAPACITY_OS_UNKNOWN',
] as const satisfies readonly RozvadecWeldLayoutIssueCode[];

export function isRozvadecWeldLayoutCompletenessIssue(
  code: string | null | undefined,
): boolean {
  return (ROZVADEC_WELD_LAYOUT_COMPLETENESS_ISSUE_CODES as readonly string[]).includes(
    (code ?? '').trim(),
  );
}

/**
 * Combinable primary modes. `directEnabled` marks OS / weld-tray cabinets.
 * New cassette-bypass incoming→building links are forbidden everywhere —
 * cable–cable belongs in KM4/KM6 seats or the OS occupancy tray. Address
 * cabinets keep `connectorsEnabled`. Stored bypass directs on non-OS are
 * disconnected. OS (and untyped cabinets) force direct-only occupancy.
 */
export type RozvadecWeldLayoutOffers = {
  directEnabled: boolean;
  connectorsEnabled: boolean;
};

/** Incoming feeder pair. `physicalFiber` is 1-based. */
export type RozvadecWeldIncomingRef = {
  cableId: string;
  physicalFiber: number;
};

/** Building záfuk target. No unit label — building + physical fiber (+ záfuk cable). */
export type RozvadecWeldTargetRef = {
  buildingId: string;
  cableId: string;
  physicalFiber: number;
};

/**
 * One installed cassette anchored at `position` (0-based, section numbering order).
 * Span-2 cassettes (KM5 family) also occupy `position + 1`.
 */
export type RozvadecWeldCassette = {
  id: string;
  cassetteType: RozvadecCassetteTypeId;
  sectionId: string;
  position: number;
};

/**
 * One termination. Cabinet types with a cassette rack require `cassetteId` +
 * 1-based `slotInCassette`; splitters on racks with `splittersOutsideCassettes`
 * (PON32) and all terminations on rack-less types are loose (`cassetteId: null`).
 *
 * Connector = through-point: `incoming` is the splice-side pigtail weld (one cable
 * fiber). Patch-side is a separate occupancy patchcord or `kind: 'connector'` link
 * — never a second bare fiber on the same optical channel.
 */
export type RozvadecWeldTermination = {
  id: string;
  kind: RozvadecWeldTerminationKind;
  cassetteId: string | null;
  slotInCassette: number | null;
  /**
   * Rear feeder fiber. Required for connectors (pigtail splice).
   * Splitters: IN port; may be unpaired (`null`) and receive a fiber later.
   */
  incoming: RozvadecWeldIncomingRef | null;
  splitterRatio: RozvadecSplitterRatio | null;
};

/**
 * Secondary building link, or a cassette-bound incoming pair (primary KM4/KM6).
 * - direct + target: incoming → building (secondary)
 * - direct + cassetteId + peerIncoming, target null: two incoming fibers in a tray
 * - direct + cassetteId, peerIncoming null, target null: pending first fiber
 * - connector: **front** of a connector termination → building (`incoming`/`peerIncoming` must be null)
 * - splitter: source is a splitter termination + 1-based **OUT** (never the IN port)
 */
export type RozvadecWeldLink = {
  id: string;
  kind: RozvadecWeldLinkKind;
  incoming: RozvadecWeldIncomingRef | null;
  /** Second incoming fiber of a cassette-bound direct pair. */
  peerIncoming?: RozvadecWeldIncomingRef | null;
  /** Weld-tray cassette that hosts this direct pair / pending fiber. */
  cassetteId?: string | null;
  /** 1-based weld seat in a KM4/KM6 tray (`Svár N`). Null on historic / building directs. */
  seat?: number | null;
  terminationId: string | null;
  splitterOutput: number | null;
  /** Building záfuk. Null for cassette-bound incoming pairs. */
  target: RozvadecWeldTargetRef | null;
};

export type RozvadecWeldLayout = {
  version: RozvadecWeldLayoutVersion;
  offers: RozvadecWeldLayoutOffers;
  cassettes: RozvadecWeldCassette[];
  terminations: RozvadecWeldTermination[];
  links: RozvadecWeldLink[];
  /** Cassette-rack fiber welds (stable endpoint IDs). Optional on historic docs. */
  weldConnections?: OccupancyWeldConnection[];
  /** Cross-cassette patchcords (SC ↔ splitter IN, numbered OUT ↔ LC). */
  patchConnections?: OccupancyPatchConnection[];
  /** Splitter IN/OUT occupancy for D5 (each OUT is a separate hop). */
  splitterPaths?: OccupancySplitterPath[];
};

export type RozvadecWeldLayoutCable = {
  cableId: string;
  typOk?: string | null;
  /** Explicit fiber count wins over `typOk` when both are set. */
  fiberCount?: number | null;
  /** Upstream cabinet on this feeder záfuk — display only, never invented. */
  sourceRozvadecObjectId?: string | null;
  sourceRozvadecName?: string | null;
  /** Downstream cabinet when this cable is an outgoing primary záfuk. */
  destRozvadecObjectId?: string | null;
  destRozvadecName?: string | null;
};

export type RozvadecWeldLayoutBuilding = {
  buildingId: string;
  cableId?: string | null;
  typOk?: string | null;
  fiberCount?: number | null;
};

export type RozvadecWeldLayoutContext = {
  typeId?: string | null;
  derivedFromSap?: string | null;
  materials?: readonly RozvadecMaterialUsageLike[] | null;
  incomingCables?: readonly RozvadecWeldLayoutCable[] | null;
  /** Optical záfuk leaving this cabinet toward peers that selected it as „zafouknuto z“. */
  outgoingCables?: readonly RozvadecWeldLayoutCable[] | null;
  buildings?: readonly RozvadecWeldLayoutBuilding[] | null;
  cassettes?: readonly RozvadecWeldCassette[] | null;
};

export type RozvadecWeldLayoutIssue = {
  code: RozvadecWeldLayoutIssueCode;
  path: string;
  message: string;
};

export type RozvadecWeldCassetteCountsByType = Record<RozvadecCassetteTypeId, number>;

export type RozvadecWeldLayoutCounts = {
  cassetteCount: number;
  cassetteCountsByType: RozvadecWeldCassetteCountsByType;
  /** Aggregate weld tray capacity of installed KM4/KM6 cassettes. */
  cassetteWeldCapacity: number;
  /** Complete incoming↔incoming pairs hosted in KM4/KM6 trays. */
  cassettePairCount: number;
  terminationCount: number;
  connectorTerminationCount: number;
  splitterTerminationCount: number;
  linkCount: number;
  directLinkCount: number;
  connectorLinkCount: number;
  splitterOutputLinkCount: number;
  /** Occupied connector/splitter inputs plus complete cassette incoming pairs. */
  weldCountPrimary: number;
  /** Building-targeted links (direct / connector / splitter outputs). */
  weldCountSecondary: number;
  /** Breakdown total (`primary + secondary`) — not used for montáž price. */
  weldCount: number;
  /** Unique stored physical `weld` rows (billing / preview). */
  weldCountTotal: number;
};

export type RozvadecWeldFreeSplitterOutput = {
  terminationId: string;
  splitterOutput: number;
};

export type RozvadecWeldFreeCassetteSlot = {
  cassetteId: string;
  slotInCassette: number;
};

export type RozvadecWeldLayoutOccupancy = {
  /** `sectionId#position` keys for every rack position covered by a cassette. */
  occupiedRackPositionKeys: string[];
  /** `cassetteId#slot` keys occupied by terminations. */
  occupiedCassetteSlotKeys: string[];
  occupiedIncomingKeys: string[];
  terminatedIncomingKeys: string[];
  directIncomingKeys: string[];
  occupiedTargetKeys: string[];
  occupiedSplitterOutputKeys: string[];
  freeIncoming: RozvadecWeldIncomingRef[];
  availableDirectSources: RozvadecWeldIncomingRef[];
  freeConnectorTerminationIds: string[];
  freeSplitterOutputs: RozvadecWeldFreeSplitterOutput[];
  /** Free adapter / preinstalled-splitter slots across installed cassettes. */
  freeCassetteSlots: RozvadecWeldFreeCassetteSlot[];
};

export type RozvadecWeldLayoutCapacity = {
  primaryLimit: number | null;
  secondaryLimit: number | null;
  unknownOsLimit: boolean;
  catalogWeldCapacity: number | null;
  maxWeldCount: number | null;
  /** SDF fiber field / RAY-OFSM connector sum — never treated as weld cap. */
  connectorLimit: number | null;
  weldLimit: number | null;
};

export type RozvadecWeldLayoutCompletion = {
  complete: boolean;
  counts: RozvadecWeldLayoutCounts;
  issues: RozvadecWeldLayoutIssue[];
  unknownOsCapacity: boolean;
};

export type RozvadecWeldLayoutNormalizeResult = {
  layout: RozvadecWeldLayout;
  hadUnitLabel: boolean;
};

export type RozvadecWeldLayoutParityExpect = {
  issueCodes: RozvadecWeldLayoutIssueCode[];
  counts: Pick<
    RozvadecWeldLayoutCounts,
    'weldCountPrimary' | 'weldCountSecondary' | 'cassetteCount' | 'cassetteWeldCapacity'
  >;
  occupiedRackPositionKeys: string[];
  occupiedCassetteSlotKeys: string[];
  freeIncomingKeys: string[];
  completePrimary: boolean;
  completeSecondary: boolean;
  unknownOsCapacity: boolean;
};

export type RozvadecWeldLayoutParityFixture = {
  id: string;
  title: string;
  raw?: unknown;
  layout: RozvadecWeldLayout;
  context: RozvadecWeldLayoutContext;
  expect: RozvadecWeldLayoutParityExpect;
};

export type RozvadecWeldLayoutExportPayload = {
  contractVersion: RozvadecWeldLayoutVersion;
  cassettePositionBase: number;
  cassetteSlotBase: number;
  splitterOutputBase: number;
  terminationKinds: readonly RozvadecWeldTerminationKind[];
  linkKinds: readonly RozvadecWeldLinkKind[];
  issueCodes: readonly RozvadecWeldLayoutIssueCode[];
  offerRules: {
    combinable: true;
    osForcesDirectOnly: true;
    newDirectsOsOnly: true;
    primaryDirectCreatesNoTermination: true;
    cassettePairsArePrimaryDirects: true;
    noUnitLabel: true;
  };
  countSemantics: {
    primary: 'terminations + cassette pairs';
    secondary: 'building-targeted links';
  };
  fixtures: RozvadecWeldLayoutParityFixture[];
};

export function isRozvadecWeldLayoutVersion(value: unknown): value is RozvadecWeldLayoutVersion {
  return value === ROZVADEC_WELD_LAYOUT_VERSION;
}

export function isRozvadecWeldTerminationKind(
  value: unknown,
): value is RozvadecWeldTerminationKind {
  return (
    typeof value === 'string' &&
    (ROZVADEC_WELD_TERMINATION_KINDS as readonly string[]).includes(value.trim())
  );
}

export function isRozvadecWeldLinkKind(value: unknown): value is RozvadecWeldLinkKind {
  return (
    typeof value === 'string' && (ROZVADEC_WELD_LINK_KINDS as readonly string[]).includes(value.trim())
  );
}

export function isRozvadecWeldLayoutIssueCode(value: unknown): value is RozvadecWeldLayoutIssueCode {
  return (
    typeof value === 'string' &&
    (ROZVADEC_WELD_LAYOUT_ISSUE_CODES as readonly string[]).includes(value)
  );
}
